import { z } from "zod";
import { DeliveryStatus } from "@carry/shared";
import { PublicIdSchema } from "./common";

/**
 * F14 Slice 3: 配送ステータス申請系操作の監査ログ.
 * - revert: completed → in_transit のステータス巻き戻し (回数制限なし)
 * - timestamp_edit: 当日内の打刻修正 (承認済日報配下は不可)
 * - backfill: 過去日の配送実績の後追い登録 (#769、申請 → 管理者承認で反映)
 */

// === Input schemas ===

export const RevertStopStatusInput = z.object({
  reason: z.string().max(500).optional(),
});
export type RevertStopStatusInputDto = z.infer<typeof RevertStopStatusInput>;

export const EditStopTimestampsInput = z
  .object({
    pickup_completed_at: z.iso.datetime({ offset: true }).optional(),
    completed_at: z.iso.datetime({ offset: true }).optional(),
    reason: z.string().min(1, "理由を入力してください").max(500),
  })
  .refine((v) => v.pickup_completed_at !== undefined || v.completed_at !== undefined, {
    message: "pickup_completed_at と completed_at の少なくとも一方を指定してください",
  });
export type EditStopTimestampsInputDto = z.infer<typeof EditStopTimestampsInput>;

// === Output schemas ===

/**
 * 巻き戻しレスポンス. previous_status は revert 直前の status (=completed)。
 * audit_id は本操作で記録された delivery_status_audit の public_id。
 */
export const RevertStopStatusOutput = z.object({
  id: PublicIdSchema,
  status: DeliveryStatus,
  previous_status: DeliveryStatus,
  reverted_at: z.iso.datetime({ offset: true }),
  audit_id: PublicIdSchema,
});

export const EditStopTimestampsOutput = z.object({
  id: PublicIdSchema,
  pickup_completed_at: z.iso.datetime({ offset: true }).nullable(),
  completed_at: z.iso.datetime({ offset: true }).nullable(),
  audit_ids: z.array(PublicIdSchema),
});

// === Audit timeline (F10 申請履歴タブ用) ===

const ActorSchema = z.object({
  id: PublicIdSchema,
  name: z.string(),
});

/**
 * 申請履歴の 1 エントリ. delay_report は Slice 2 でマージ追加される.
 * type で discriminated union 分岐.
 */
export const DeliveryAuditEntry = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("delay_report"),
    occurred_at: z.iso.datetime({ offset: true }),
    actor: ActorSchema,
    payload: z.object({
      report_id: PublicIdSchema,
      delay_reason: z.enum(["traffic", "production_delay", "previous_case_delay", "other"]),
      eta_type: z.enum(["unknown", "specified"]),
      eta_at: z.iso.datetime({ offset: true }).nullable(),
      impact_note: z.string().nullable(),
      resolved_at: z.iso.datetime({ offset: true }).nullable(),
    }),
  }),
  z.object({
    type: z.literal("timestamp_edit"),
    occurred_at: z.iso.datetime({ offset: true }),
    actor: ActorSchema,
    payload: z.object({
      field: z.string(),
      before: z.string().nullable(),
      after: z.string().nullable(),
      reason: z.string().nullable(),
    }),
  }),
  // #769: 過去日の配送実績の後追い登録 (承認時に反映した内容)。actor は承認者、
  // requested_by は申請したドライバー (管理者の代理入力では両方が同じ人になる)。
  z.object({
    type: z.literal("backfill"),
    occurred_at: z.iso.datetime({ offset: true }),
    actor: ActorSchema,
    payload: z.object({
      request_id: PublicIdSchema,
      work_date: z.iso.date(),
      field: z.string(),
      before: z.string().nullable(),
      after: z.string().nullable(),
      reason: z.string().nullable(),
      requested_by: ActorSchema.nullable(),
    }),
  }),
  z.object({
    type: z.literal("status_revert"),
    occurred_at: z.iso.datetime({ offset: true }),
    actor: ActorSchema,
    payload: z.object({
      from: DeliveryStatus,
      to: DeliveryStatus,
      reason: z.string().nullable(),
    }),
  }),
]);
export type DeliveryAuditEntryDto = z.infer<typeof DeliveryAuditEntry>;

export const StopAuditTimeline = z.object({
  stop_id: PublicIdSchema,
  entries: z.array(DeliveryAuditEntry),
});
export type StopAuditTimelineDto = z.infer<typeof StopAuditTimeline>;
